import React, { useState, useEffect } from 'react';
import { Printer, Loader2, BookOpen, ChevronDown, AlertCircle, Users, MessageSquare, Save, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { ReportCard } from '../student/ReportCardPage';
import api from '../../api';

export default function BulkReportPage() {
  const [classes, setClasses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [selectedTermId, setSelectedTermId] = useState('');
  const [cumulative, setCumulative] = useState(false);
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [fetched, setFetched] = useState(false);

  // Principal remarks state
  const [showRemarks, setShowRemarks] = useState(false);
  const [principalRemarks, setPrincipalRemarks] = useState({});    // studentId -> draft
  const [savedRemarks, setSavedRemarks] = useState({});            // studentId -> saved comment
  const [savingRemark, setSavingRemark] = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/academics/classes'),
      api.get('/academics/current-session'),
    ]).then(([clsRes, sesRes]) => {
      setClasses(clsRes.data || []);
      const tList = sesRes.data?.terms_for_year || [];
      setTerms(tList);
      if (sesRes.data?.term) {
        setSelectedTermId(sesRes.data.term.id);
        setCumulative((sesRes.data.term.name || '').toLowerCase().includes('third'));
      }
    }).catch(() => toast.error('Failed to load data'));
  }, []);

  // Load existing principal comments when class+term selected
  useEffect(() => {
    if (!selectedClass || !selectedTermId) return;
    api.get(`/academics/student-comments?class_id=${selectedClass}&term_id=${selectedTermId}`)
      .then(res => {
        const map = {};
        (res.data || []).filter(c => c.comment_type === 'principal').forEach(c => { map[c.student_id] = c.comment; });
        setSavedRemarks(map);
        setPrincipalRemarks({});
      }).catch(() => {});
  }, [selectedClass, selectedTermId]);

  const loadCards = async () => {
    if (!selectedClass) { toast.error('Please select a class'); return; }
    setLoading(true);
    setFetched(false);
    try {
      const res = await api.get(
        `/exams/class-report-cards?class_id=${selectedClass}&term_id=${selectedTermId}&cumulative=${cumulative}`
      );
      setCards(res.data || []);
      setFetched(true);
      if (!res.data?.length) toast.error('No students found in this class');
    } catch (err) {
      toast.error(err?.detail || 'Failed to load report cards');
    } finally {
      setLoading(false);
    }
  };

  const savePrincipalRemark = async (studentId) => {
    const comment = principalRemarks[studentId] ?? savedRemarks[studentId] ?? '';
    if (!comment.trim()) { toast.error('Remark is empty'); return; }
    setSavingRemark(studentId);
    try {
      await api.post('/academics/student-comments', {
        student_id: studentId,
        term_id: selectedTermId,
        comment: comment.trim(),
        comment_type: 'principal',
      });
      setSavedRemarks(p => ({ ...p, [studentId]: comment.trim() }));
      setPrincipalRemarks(p => { const n = { ...p }; delete n[studentId]; return n; });
      toast.success('Principal remark saved');
    } catch (err) { toast.error(err?.detail || 'Failed to save'); }
    finally { setSavingRemark(''); }
  };

  const saveAllRemarks = async () => {
    const toSave = cards.filter(c => principalRemarks[c.student_id]?.trim());
    if (!toSave.length) { toast.info('No new remarks to save'); return; }
    setSavingRemark('all');
    let saved = 0;
    for (const card of toSave) {
      try {
        await api.post('/academics/student-comments', {
          student_id: card.student_id,
          term_id: selectedTermId,
          comment: principalRemarks[card.student_id].trim(),
          comment_type: 'principal',
        });
        setSavedRemarks(p => ({ ...p, [card.student_id]: principalRemarks[card.student_id].trim() }));
        saved++;
      } catch {}
    }
    setPrincipalRemarks({});
    setSavingRemark('');
    toast.success(`${saved} remark${saved !== 1 ? 's' : ''} saved`);
  };

  const className = classes.find(c => c.id === selectedClass)?.name || '';
  const termName = terms.find(t => t.id === selectedTermId)?.name || '';
  const pendingRemarks = cards.filter(c => principalRemarks[c.student_id]?.trim()).length;

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="bulk-report-page">
      {/* Header */}
      <div className="no-print mb-6">
        <h1 className="text-xl font-bold text-slate-900">Bulk Report Cards</h1>
        <p className="text-sm text-slate-500 mt-0.5">Generate and print all report cards for a class at once</p>
      </div>

      {/* Controls */}
      <div className="no-print bg-white rounded-xl border border-slate-200 p-5 mb-6 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Class</label>
          <div className="relative">
            <select value={selectedClass} onChange={e => { setSelectedClass(e.target.value); setFetched(false); }}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:border-indigo-400 appearance-none pr-8 min-w-[160px]"
              data-testid="bulk-class-select">
              <option value="">Select class...</option>
              {classes.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>

        <div>
          <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide">Term</label>
          <div className="relative">
            <select value={selectedTermId} onChange={e => {
              setSelectedTermId(e.target.value);
              const t = terms.find(t => t.id === e.target.value);
              setCumulative((t?.name || '').toLowerCase().includes('third'));
              setFetched(false);
            }}
              className="border border-slate-200 rounded-lg px-3 py-2 text-sm bg-white outline-none focus:border-indigo-400 appearance-none pr-8 min-w-[160px]"
              data-testid="bulk-term-select">
              {terms.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>

        <div className="flex items-center gap-2 pt-4">
          <input type="checkbox" id="cumulative-check" checked={cumulative} onChange={e => { setCumulative(e.target.checked); setFetched(false); }}
            className="w-4 h-4 text-indigo-600 rounded" />
          <label htmlFor="cumulative-check" className="text-sm text-slate-700 font-medium">Cumulative</label>
        </div>

        <button onClick={loadCards} disabled={loading || !selectedClass}
          className="flex items-center gap-2 px-5 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          data-testid="load-cards-btn">
          {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Loading...</> : <><Users className="w-4 h-4" /> Load Report Cards</>}
        </button>

        {fetched && cards.length > 0 && (
          <>
            <button onClick={() => setShowRemarks(v => !v)}
              className={`flex items-center gap-2 px-5 py-2 text-sm font-semibold rounded-lg border transition-colors ${showRemarks ? 'bg-violet-600 text-white border-violet-600' : 'bg-white text-slate-700 border-slate-300 hover:border-violet-400'}`}
              data-testid="toggle-remarks-btn">
              <MessageSquare className="w-4 h-4" />
              Principal Remarks
            </button>
            <button onClick={() => window.print()}
              className="flex items-center gap-2 px-5 py-2 bg-emerald-600 text-white text-sm font-semibold rounded-lg hover:bg-emerald-700 transition-colors"
              data-testid="bulk-print-btn">
              <Printer className="w-4 h-4" /> Print All ({cards.length})
            </button>
          </>
        )}
      </div>

      {fetched && cards.length === 0 && (
        <div className="no-print bg-amber-50 border border-amber-200 rounded-xl p-8 text-center">
          <AlertCircle className="w-10 h-10 mx-auto mb-3 text-amber-400" />
          <p className="font-semibold text-amber-800">No students found</p>
          <p className="text-sm text-amber-600 mt-1">This class has no enrolled students with results.</p>
        </div>
      )}

      {/* Principal Remarks Panel */}
      {showRemarks && cards.length > 0 && (
        <div className="no-print bg-white rounded-xl border border-violet-200 mb-6 overflow-hidden shadow-sm" data-testid="principal-remarks-panel">
          <div className="bg-violet-50 border-b border-violet-100 px-5 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <MessageSquare className="w-4 h-4 text-violet-600" />
              <div>
                <p className="font-bold text-violet-800 text-sm">Principal Remarks</p>
                <p className="text-xs text-violet-600">{className} · {termName} · {Object.keys(savedRemarks).length}/{cards.length} written</p>
              </div>
            </div>
            {pendingRemarks > 0 && (
              <button onClick={saveAllRemarks} disabled={savingRemark === 'all'}
                className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                data-testid="save-all-remarks-btn">
                {savingRemark === 'all' ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Save All ({pendingRemarks})
              </button>
            )}
          </div>
          <div className="divide-y divide-slate-100">
            {cards.map(card => {
              const existing = savedRemarks[card.student_id];
              const draft = principalRemarks[card.student_id];
              const display = draft !== undefined ? draft : (existing || '');
              const isDirty = draft !== undefined && draft !== existing;
              return (
                <div key={card.student_id} className={`flex items-start gap-4 px-5 py-3 ${isDirty ? 'bg-violet-50/40' : ''}`}>
                  <div className="w-8 h-8 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-violet-700">{card.student_name?.charAt(0)}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="text-sm font-semibold text-slate-800">{card.student_name}</p>
                      {existing && !isDirty && <span className="text-xs text-green-600 font-semibold flex items-center gap-1"><CheckCircle className="w-3 h-3" />Saved</span>}
                      {isDirty && <span className="text-xs text-amber-600 font-semibold">Unsaved</span>}
                    </div>
                    <textarea
                      value={display}
                      onChange={e => setPrincipalRemarks(p => ({ ...p, [card.student_id]: e.target.value }))}
                      placeholder="Principal's remark for this student's report card..."
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm outline-none resize-none h-14 focus:border-violet-400 transition-colors"
                      data-testid={`principal-remark-${card.student_id}`}
                    />
                  </div>
                  <button onClick={() => savePrincipalRemark(card.student_id)}
                    disabled={savingRemark === card.student_id || !display.trim()}
                    className="mt-7 flex items-center gap-1 bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold px-3 py-2 rounded-lg disabled:opacity-40 transition-colors flex-shrink-0"
                    data-testid={`save-remark-${card.student_id}`}>
                    {savingRemark === card.student_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {cards.length > 0 && (
        <>
          {/* Print title - only visible in print */}
          <div className="hidden print:block mb-4">
            <h1 className="text-xl font-bold text-center">{className} — {termName} Report Cards</h1>
          </div>

          <div className="no-print mb-4 flex items-center gap-2 text-sm text-slate-600 bg-green-50 border border-green-200 rounded-lg p-3">
            <BookOpen className="w-4 h-4 text-green-600" />
            <span><strong>{cards.length}</strong> report cards loaded for <strong>{className}</strong> — Click "Print All" to generate PDF</span>
          </div>

          <div className="space-y-8 print-cards">
            {cards.map((card, i) => (
              <div key={i} className="print-page-break">
                <ReportCard data={card} />
              </div>
            ))}
          </div>
        </>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { background: white !important; margin: 0; padding: 0; }
          .print-page-break { page-break-after: always; margin-bottom: 0; }
          .print-page-break:last-child { page-break-after: avoid; }
          .report-card-wrapper { border: none !important; box-shadow: none !important; border-radius: 0 !important; }
        }
      `}</style>
    </div>
  );
}
